import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import MwInputText from "../components/ui/MwInputText";
import MwButton from "../components/ui/MwButton";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import JournalEntryContext from "../utils/JournalEntryHelper";
import {
  CardHeadeWithActions,
  MwSelector,
  MwSpinnerButton,
  MwTextArea,
} from "../components/ui";
import i18next from "i18next";
import SelectorTree from "../components/ui/SelectAccount/SelectorTree";
import AccountContext from "../utils/AccountsHelper";
import AccountCenterContext from "../utils/CostCenterAccHelper";
import CurrencyContext from "../utils/CurrencyHelper";
import { useTenant } from "../hooks";
import useUser from "../hooks/useUser";
import { Tabs } from "../components/ui/Tabs";
import AttachedFiles from "../components/ui/AttachedFiles";

const getTodayDate = () => {
  const today = new Date();
  today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
  return today.toISOString().split("T")[0];
};

const createLineId = () =>
  `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyLine = () => ({
  lineId: createLineId(),
  account_no: "",
  accountName: "",
  trans_de: "",
  trans_cr: "",
  cost_center_no: "",
  costCenterLabel: "",
  trans_notes: "",
});

const hydrateLines = (lines) => {
  let source = [];

  if (Array.isArray(lines) && lines.length) {
    source = lines;
  } else if (lines && typeof lines === "object" && !Array.isArray(lines)) {
    source = [lines];
  } else {
    source = [createEmptyLine()];
  }

  return source
    .filter((line) => line !== null && line !== undefined)
    .map((line) => ({
      ...createEmptyLine(),
      ...line,
      lineId: line?.lineId || createLineId(),
    }));
};

const normalizeLine = (line) => {
  const debitValue = Number(line?.trans_de || 0);
  const creditValue = Number(line?.trans_cr || 0);

  return {
    ...line,
    account_no: String(
      line?.account_no || line?.accountNumber || line?.account_number || "",
    ).trim(),
    accountName: String(
      line?.accountName || line?.account_name || line?.name || "",
    ).trim(),
    trans_de: Number.isFinite(debitValue) ? debitValue : 0,
    trans_cr: Number.isFinite(creditValue) ? creditValue : 0,
    cost_center_no: String(
      line?.cost_center_no ||
        line?.costCenterNumber ||
        line?.cost_center_number ||
        "",
    ).trim(),
    costCenterLabel: String(
      line?.costCenterLabel ||
        line?.cost_center_name ||
        line?.costCenterName ||
        "",
    ).trim(),
    trans_notes: String(line?.trans_notes || "").trim(),
  };
};

const isLineEmpty = (line) =>
  !line.account_no &&
  !line.accountName &&
  !line.cost_center_no &&
  !line.costCenterLabel &&
  !line.trans_notes &&
  line.trans_de <= 0 &&
  line.trans_cr <= 0;

const normalizeAttachmentValue = (file) => {
  if (typeof file === "string") return file.trim();
  if (!file || typeof file !== "object") return "";

  const candidate =
    file.url ||
    file.location ||
    file.path ||
    file.keyFile ||
    file.key ||
    file.file ||
    file.name ||
    "";

  return typeof candidate === "string" ? candidate.trim() : "";
};

const normalizeAttachFiles = (value) => {
  if (!value) return [];

  const values = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[;,]/)
      : [value];

  const seen = new Set();

  return values
    .map((item) => normalizeAttachmentValue(item))
    .filter(Boolean)
    .filter((item) => {
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    });
};

const initialState = {
  trans_no: "",
  trans_date_m: getTodayDate(),
  trans_ref_no: "",
  trans_desc: "",
  trans_currency: 1,
  cost_center_no: "",
  costCenterLabel: "",
  trans_currency_rate: 1,
  is_completed: 0,
  attach_files: [],
  details: [createEmptyLine()],
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_ALL":
      const detailsToUse =
        action.value?.details ||
        action.value?.lines ||
        action.value?.items ||
        [];
      const attachFilesToUse = normalizeAttachFiles(action.value?.attach_files);
      return {
        ...state,
        ...action.value,
        attach_files: attachFilesToUse,
        details: hydrateLines(detailsToUse),
      };

    case "SET_FIELD":
      return { ...state, [action.field]: action.value };

    case "UPDATE_LINE_FIELD":
      return {
        ...state,
        details: state.details.map((line, index) =>
          index === action.index
            ? { ...line, [action.field]: action.value }
            : line,
        ),
      };

    case "UPDATE_LINE":
      return {
        ...state,
        details: state.details.map((line, index) =>
          index === action.index ? { ...line, ...action.value } : line,
        ),
      };

    case "ADD_EMPTY_LINE":
      return { ...state, details: [...state.details, createEmptyLine()] };

    case "DELETE_LINE":
      if (state.details.length <= 1) {
        return { ...state, details: [createEmptyLine()] };
      }
      return {
        ...state,
        details: state.details.filter((_, i) => i !== action.index),
      };

    case "RESET":
      return {
        ...initialState,
        trans_date_m: getTodayDate(),
        details: [createEmptyLine()],
      };

    default:
      return state;
  }
};

const normalizeAccountType = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "sub") return "secondary";
  if (normalized === "secondary") return "secondary";
  if (normalized === "primary") return "primary";
  return "";
};

const resolveNodeType = (node) => {
  const explicitType = normalizeAccountType(
    node?.accountType || node?.account_type,
  );
  if (explicitType) return explicitType;
  const children = node?.children || node?.childs;
  return Array.isArray(children) && children.length > 0
    ? "primary"
    : "secondary";
};

const flattenAccountOptions = (nodes, lang, list = []) => {
  (nodes || []).forEach((node) => {
    const accountNumber =
      node?.account_no ||
      node?.accountNumber ||
      node?.account_number ||
      String(node?.id || "");
    const accountName =
      lang === "ar"
        ? node?.name_ar ||
          node?.accountNameAR ||
          node?.accountNameEN ||
          node?.name_en ||
          node?.accountName ||
          "-"
        : node?.name_en ||
          node?.accountNameEN ||
          node?.accountNameAR ||
          node?.name_ar ||
          node?.accountName ||
          "-";

    list.push({
      id: node?.id,
      value: accountNumber,
      label: accountName,
      accountNumber,
      accountName,
      lang,
      accountType: resolveNodeType(node),
    });

    const children = node?.children || node?.childs;
    if (children?.length) {
      flattenAccountOptions(children, lang, list);
    }
  });

  return list;
};

const buildLookupByAccountNumber = (options = []) => {
  const lookup = new Map();
  options.forEach((option) => {
    const accountNumber = String(
      option?.accountNumber || option?.value || "",
    ).trim();
    if (accountNumber) {
      lookup.set(accountNumber, option);
    }
  });
  return lookup;
};

const getLocalizedLookupLabel = (source, lang) => {
  if (!source || typeof source !== "object") return "";

  const label =
    lang === "ar"
      ? source?.name_ar ||
        source?.accountNameAR ||
        source?.name_en ||
        source?.accountNameEN ||
        source?.accountName ||
        source?.name
      : source?.name_en ||
        source?.accountNameEN ||
        source?.name_ar ||
        source?.accountNameAR ||
        source?.accountName ||
        source?.name;

  return String(label || "").trim();
};

const resolveLineAccountName = (line, accountMeta, lang) =>
  String(
    accountMeta?.accountName ||
      accountMeta?.label ||
      line?.accountName ||
      line?.account_name ||
      line?.name ||
      getLocalizedLookupLabel(line?.tree_relation, lang) ||
      getLocalizedLookupLabel(line?.account_relation, lang) ||
      "",
  ).trim();

const resolveLineCostCenterLabel = (line, costCenterMeta, lang) =>
  String(
    costCenterMeta?.accountName ||
      costCenterMeta?.label ||
      line?.costCenterLabel ||
      line?.cost_center_name ||
      line?.costCenterName ||
      getLocalizedLookupLabel(line?.treecost_relation, lang) ||
      getLocalizedLookupLabel(line?.cost_center_relation, lang) ||
      "",
  ).trim();

const attachLineAccountLabels = (
  line,
  accountLookup,
  costCenterLookup,
  lang,
) => {
  const accountNumber = String(
    line?.account_no || line?.accountNumber || "",
  ).trim();
  const costCenterNumber = String(
    line?.cost_center_no || line?.costCenterNumber || "",
  ).trim();

  const accountMeta = accountLookup.get(accountNumber);
  const costCenterMeta = costCenterLookup.get(costCenterNumber);

  return {
    ...line,
    account_no: accountNumber,
    accountName: resolveLineAccountName(line, accountMeta, lang),
    cost_center_no: costCenterNumber,
    costCenterLabel: resolveLineCostCenterLabel(line, costCenterMeta, lang),
  };
};

export const JournallEntrymgm = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    tenantId,
    branchSelected,
    companySelected,
    getSettingsByCompanySelected,
  } = useTenant();
  const { getUser } = useUser();
  const { tenant } = useParams();
  const {
    JournalEntries,
    addEntry,
    editEntry,
    loadingSave,
    selectedPeriod,
    isPeriodClosed,
  } = useContext(JournalEntryContext);
  const { accounts } = useContext(AccountContext);
  const { accounts: costCenters } = useContext(AccountCenterContext);
  const actionData = location.state || {};
  const urlMode = String(searchParams.get("mode") || "").toUpperCase();
  const urlTransNo =
    searchParams.get("trans_no") || searchParams.get("transNo") || "";
  const urlEntryId =
    searchParams.get("id") || searchParams.get("entryId") || "";
  const mode = String(actionData?.mode || urlMode || "ADD").toUpperCase();
  const baseurl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  const effectivePeriodId = actionData?.periodId || selectedPeriod;

  const [state, dispatchForm] = useReducer(reducer, initialState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    trans_no: "",
    trans_ref_no: "",
  });
  const [lineErrors, setLineErrors] = useState({});
  const [saveValidationMsg, setSaveValidationMsg] = useState("");
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [columnSettingsStyle, setColumnSettingsStyle] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    account: true,
    trans_de: true,
    trans_cr: true,
    cost_center_no: true,
    trans_notes: true,
  });
  const columnSettingsRef = useRef(null);
  const columnSettingsMenuRef = useRef(null);
  const defaultCurrencyAppliedRef = useRef(false);
  const userSelectedCurrencyRef = useRef(false);

  const { currencies } = useContext(CurrencyContext);
  const [accountCurrency, setAccountCurrency] = useState([]);
  const [activeTab, setActiveTab] = useState(1);
  const TabsData = [
    {
      id: 1,
      labelAr: t("JournalEntry.tabs.lines"),
      labelEn: t("JournalEntry.tabs.lines"),
    },
    {
      id: 2,
      labelAr: t("JournalEntry.tabs.attachments"),
      labelEn: t("JournalEntry.tabs.attachments"),
    },
  ];
  const isDataLoading =
    !accountCurrency || accountCurrency.length === 0 || loading === true;

  useEffect(() => {
    if (Array.isArray(currencies) && currencies.length) {
      const normalized = currencies.map((currency) => ({
        id: String(currency?.id || ""),
        value: String(currency?.currency_code || currency?.code || "")
          .trim()
          .toUpperCase(),
        label:
          String(
            i18next.dir(i18next.language) === "rtl"
              ? currency?.name_ar || ""
              : currency?.name_en || "",
          ).trim() ||
          String(currency?.currency_code || currency?.code || "")
            .trim()
            .toUpperCase(),
        rate: (() => {
          const raw =
            currency?.currency_rate ??
            currency?.rate ??
            currency?.exchange_rate ??
            1;
          const parsed = Number(raw);
          return Number.isFinite(parsed) ? parsed : 1;
        })(),
        lang: i18next.language,
      }));
      setAccountCurrency(normalized);
    }
  }, [currencies]);

  const selectedCurrency = useMemo(
    () =>
      accountCurrency.find(
        (currency) => String(currency.id) === String(state.trans_currency),
      ) || null,
    [accountCurrency, state.trans_currency],
  );
  const selectedCurrencyLabel =
    selectedCurrency?.label || selectedCurrency?.value || "";
  const moneyFormatter = useMemo(() => {
    const locale = i18next.language === "ar" ? "en-US" : "en-US";
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [i18next.language]);
  const formatMoney = useCallback(
    (value) => moneyFormatter.format(Number(value || 0)),
    [moneyFormatter],
  );

  const currentEntry = useMemo(() => {
    if (mode !== "EDIT" && mode !== "VIEW") return null;
    if (actionData?.entry) return actionData.entry;
    const lookupId = actionData?.id || actionData?._id || urlEntryId;
    const lookupTransNo =
      actionData?.trans_no || actionData?.transNo || urlTransNo;

    return (
      (JournalEntries || []).find((entry) => {
        if (lookupId) {
          return String(entry.id || entry._id) === String(lookupId);
        }
        if (lookupTransNo) {
          return String(entry.trans_no) === String(lookupTransNo);
        }
        return false;
      }) || null
    );
  }, [
    mode,
    actionData?.entry,
    actionData?.id,
    actionData?._id,
    actionData?.trans_no,
    actionData?.transNo,
    urlEntryId,
    urlTransNo,
    JournalEntries,
  ]);

  const isPostedLock =
    mode === "EDIT" &&
    Number(
      actionData?.entry?.is_completed ?? currentEntry?.is_completed ?? 0,
    ) === 1;
  const isReadOnly = mode === "VIEW" || isPeriodClosed || isPostedLock;
  const isStatusDisabled = mode === "VIEW" || isPeriodClosed;
  const canSave = mode !== "VIEW" && !isPeriodClosed;

  const currentLanguage = i18next.language === "ar" ? "ar" : "en";
  const accountOptions = useMemo(
    () => flattenAccountOptions(accounts, currentLanguage, []),
    [accounts, currentLanguage],
  );
  const costCenterOptions = useMemo(
    () => flattenAccountOptions(costCenters, currentLanguage, []),
    [costCenters, currentLanguage],
  );
  const selectionControl = useMemo(
    () => ({
      accountAllowedTypes: ["secondary"],
      costCenterAllowedTypes: ["secondary"],
    }),
    [],
  );
  const allowedAccountTypeSet = useMemo(
    () =>
      new Set(
        selectionControl.accountAllowedTypes
          .map((type) => normalizeAccountType(type))
          .filter(Boolean),
      ),
    [selectionControl.accountAllowedTypes],
  );
  const allowedCostCenterTypeSet = useMemo(
    () =>
      new Set(
        selectionControl.costCenterAllowedTypes
          .map((type) => normalizeAccountType(type))
          .filter(Boolean),
      ),
    [selectionControl.costCenterAllowedTypes],
  );
  const selectableAccountOptions = useMemo(
    () =>
      accountOptions.filter(
        (account) =>
          allowedAccountTypeSet.size === 0 ||
          allowedAccountTypeSet.has(normalizeAccountType(account.accountType)),
      ),
    [accountOptions, allowedAccountTypeSet],
  );
  const selectableCostCenterOptions = useMemo(
    () =>
      costCenterOptions.filter(
        (center) =>
          allowedCostCenterTypeSet.size === 0 ||
          allowedCostCenterTypeSet.has(
            normalizeAccountType(center.accountType),
          ),
      ),
    [costCenterOptions, allowedCostCenterTypeSet],
  );
  const accountLookup = useMemo(
    () => buildLookupByAccountNumber(accountOptions),
    [accountOptions],
  );
  const costCenterLookup = useMemo(
    () => buildLookupByAccountNumber(costCenterOptions),
    [costCenterOptions],
  );
  const selectableAccountLookup = useMemo(
    () => buildLookupByAccountNumber(selectableAccountOptions),
    [selectableAccountOptions],
  );
  const selectableCostCenterLookup = useMemo(
    () => buildLookupByAccountNumber(selectableCostCenterOptions),
    [selectableCostCenterOptions],
  );
  const enrichLineWithLabels = useCallback(
    (line) =>
      attachLineAccountLabels(
        line,
        accountLookup,
        costCenterLookup,
        currentLanguage,
      ),
    [accountLookup, costCenterLookup, currentLanguage],
  );

  const normalizedLines = useMemo(
    () => state.details.map((line) => normalizeLine(line)),
    [state.details],
  );
  const filledLines = useMemo(
    () => normalizedLines.filter((line) => !isLineEmpty(line)),
    [normalizedLines],
  );

  const totalDebit = filledLines.reduce(
    (sum, l) => sum + Number(l.trans_de || 0),
    0,
  );
  const totalCredit = filledLines.reduce(
    (sum, l) => sum + Number(l.trans_cr || 0),
    0,
  );
  const isBalanced =
    filledLines.length > 0 &&
    parseFloat(totalDebit.toFixed(2)) === parseFloat(totalCredit.toFixed(2));
  const mainCurrencyId = useMemo(() => {
    const normalizeCode = (value) =>
      String(value || "")
        .trim()
        .toUpperCase();
    const mainCurrencyCode = normalizeCode(
      getSettingsByCompanySelected?.mainCurrency ||
        getSettingsByCompanySelected?.currency,
    );
    if (!mainCurrencyCode) return null;
    return (
      accountCurrency.find(
        (currency) => normalizeCode(currency.value) === mainCurrencyCode,
      )?.id || null
    );
  }, [accountCurrency, getSettingsByCompanySelected]);

  useEffect(() => {
    const mainCurrencyCode = String(
      getSettingsByCompanySelected?.mainCurrency ||
        getSettingsByCompanySelected?.currency ||
        "",
    )
      .trim()
      .toUpperCase();
    if (!mainCurrencyCode && !mainCurrencyId) return;
    console.log("JournalEntry mainCurrency", {
      mainCurrencyCode,
      mainCurrencyId,
      selectedCurrencyId: state.trans_currency,
    });
  }, [getSettingsByCompanySelected, mainCurrencyId, state.trans_currency]);

  const tableColumns = [
    { key: "account", label: t("accounts.account") },
    { key: "trans_de", label: t("JournalEntry.debit") },
    { key: "trans_cr", label: t("JournalEntry.credit") },
    { key: "cost_center_no", label: t("accounts.costCenterAccount") },
    { key: "trans_notes", label: t("JournalEntry.notes") },
  ];

  const updateColumnSettingsPosition = useCallback(() => {
    if (!columnSettingsRef.current) return;

    const rect = columnSettingsRef.current.getBoundingClientRect();
    const menuWidth = 224;
    const viewportPadding = 8;
    const isRtl = i18next.dir(i18next.language) === "rtl";
    const preferredLeft = isRtl ? rect.left : rect.right - menuWidth;
    const maxLeft = Math.max(
      viewportPadding,
      window.innerWidth - menuWidth - viewportPadding,
    );
    const left = Math.max(viewportPadding, Math.min(preferredLeft, maxLeft));
    const hasBottomSpace = window.innerHeight - rect.bottom > 260;

    setColumnSettingsStyle({
      position: "fixed",
      top: hasBottomSpace ? rect.bottom + 4 : rect.top - 4,
      left,
      transform: hasBottomSpace ? "none" : "translateY(-100%)",
      zIndex: 2147483646,
      width: menuWidth,
    });
  }, []);

  const handleClose = () => {
    if (tenant) {
      navigate(`/${tenant}/JournalEntry`);
      return;
    }
    navigate(-1);
  };

  const setLineFieldError = (lineIndex, field, value) => {
    setLineErrors((prev) => {
      const current = {
        account: false,
        amount: false,
        costCenter: false,
        ...(prev[lineIndex] || {}),
      };
      current[field] = value;

      const next = { ...prev };
      if (!current.account && !current.amount && !current.costCenter) {
        delete next[lineIndex];
      } else {
        next[lineIndex] = current;
      }
      return next;
    });
  };

  const handleSelectAccount = (lineIndex, selected) => {
    const selectedAccountNumber = String(
      selected?.account_no || selected?.accountNumber || selected?.value || "",
    ).trim();

    dispatchForm({
      type: "UPDATE_LINE",
      index: lineIndex,
      value: {
        account_no: selectedAccountNumber,
        accountName:
          selected?.accountName || selected?.label || selected?.name || "",
      },
    });
    setLineFieldError(lineIndex, "account", false);
  };

  const handleManualAccountNumberInput = useCallback(
    (lineIndex, inputValue) => {
      const accountNumber = String(inputValue || "").trim();
      const matched = selectableAccountLookup.get(accountNumber);

      dispatchForm({
        type: "UPDATE_LINE",
        index: lineIndex,
        value: {
          account_no: accountNumber,
          accountName: matched?.accountName || matched?.label || "",
        },
      });
      setLineFieldError(
        lineIndex,
        "account",
        Boolean(accountNumber && !matched),
      );
    },
    [selectableAccountLookup],
  );

  const handleSelectCostCenter = (lineIndex, selected) => {
    const selectedCostCenterNumber = String(
      selected?.account_no || selected?.accountNumber || selected?.value || "",
    ).trim();

    dispatchForm({
      type: "UPDATE_LINE",
      index: lineIndex,
      value: {
        cost_center_no: selectedCostCenterNumber,
        costCenterLabel:
          selected?.accountName || selected?.label || selected?.name || "",
      },
    });
    setLineFieldError(lineIndex, "costCenter", false);
  };

  const handleManualLineCostCenterNumberInput = useCallback(
    (lineIndex, inputValue) => {
      const accountNumber = String(inputValue || "").trim();
      const matched = selectableCostCenterLookup.get(accountNumber);

      dispatchForm({
        type: "UPDATE_LINE",
        index: lineIndex,
        value: {
          cost_center_no: accountNumber,
          costCenterLabel: matched?.accountName || matched?.label || "",
        },
      });
      setLineFieldError(
        lineIndex,
        "costCenter",
        Boolean(accountNumber && !matched),
      );
    },
    [selectableCostCenterLookup],
  );

  const handleManualHeaderCostCenterNumberInput = useCallback(
    (inputValue) => {
      const accountNumber = String(inputValue || "").trim();
      const matched = selectableCostCenterLookup.get(accountNumber);

      dispatchForm({
        type: "SET_FIELD",
        field: "cost_center_no",
        value: accountNumber,
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "costCenterLabel",
        value: matched?.accountName || matched?.label || "",
      });
    },
    [selectableCostCenterLookup],
  );

  const handleSave = async () => {
    if (isPeriodClosed) {
      setSaveValidationMsg(`⚠️ ${t("times.closedYearWarning")}`);
      return;
    }
    setSaveValidationMsg("");
    setErrors({});
    setLineErrors({});

    const transNoTrimmed = String(state.trans_no || "").trim();
    if (!transNoTrimmed) {
      setErrors((prev) => ({
        ...prev,
        trans_no: `⚠️ ${t("JournalEntry.numofentry")} ${t("common.required")}`,
      }));
      setSaveValidationMsg(
        `⚠️ ${t("JournalEntry.numofentry")} ${t("common.required")}`,
      );
      return;
    }

    const hasAtLeastOneAccount =
      filledLines.length > 0 &&
      filledLines.some((line) => line.account_no && line.accountName);

    if (!hasAtLeastOneAccount) {
      setSaveValidationMsg(
        `⚠️ ${t("accounts.account")} ${t("common.required")}`,
      );
      setLineErrors({ 0: { account: true, amount: false, costCenter: false } });
      return;
    }

    const nextLineErrors = {};
    filledLines.forEach((line, index) => {
      const accountInvalid = !line.account_no || !line.accountName;
      const amountInvalid = line.trans_de > 0 && line.trans_cr > 0;
      const costCenterInvalid = Boolean(
        line.cost_center_no && !line.costCenterLabel,
      );

      if (accountInvalid || amountInvalid || costCenterInvalid) {
        nextLineErrors[index] = {
          account: accountInvalid,
          amount: amountInvalid,
          costCenter: costCenterInvalid,
        };
      }
    });

    if (Object.keys(nextLineErrors).length > 0) {
      setLineErrors(nextLineErrors);
      setSaveValidationMsg("⚠️ يوجد أخطاء في البيانات المدخلة");
      return;
    }

    const linesWithAmounts = filledLines.filter(
      (l) => Number(l.trans_de || 0) > 0 || Number(l.trans_cr || 0) > 0,
    );

    if (linesWithAmounts.length > 0) {
      const totalDebit = linesWithAmounts.reduce(
        (sum, l) => sum + Number(l.trans_de || 0),
        0,
      );
      const totalCredit = linesWithAmounts.reduce(
        (sum, l) => sum + Number(l.trans_cr || 0),
        0,
      );
      const isBalanced =
        parseFloat(totalDebit.toFixed(2)) ===
        parseFloat(totalCredit.toFixed(2));

      if (!isBalanced) {
        setSaveValidationMsg(
          `⚠️ ${t("JournalEntry.unbalanced")} | ${t("JournalEntry.totalDebit")}: ${totalDebit} | ${t("JournalEntry.totalCredit")}: ${totalCredit}`,
        );
        return;
      }
    }

    if (!effectivePeriodId) {
      setSaveValidationMsg("⚠️ خطأ: لم يتم تحديد السنة المالية");
      return;
    }
    if (!tenantId || !companySelected?.value || !branchSelected?.value) {
      setSaveValidationMsg("⚠️ خطأ: بيانات الشركة أو الفرع غير مكتملة");
      return;
    }
    if (!getUser?.id && !getUser?._id) {
      setSaveValidationMsg("⚠️ خطأ: معلومات المستخدم غير متاحة");
      return;
    }

    const payload = {
      fin_year_id: effectivePeriodId,
      trans_no: state.trans_no,
      trans_desc: state.trans_desc,
      trans_date_m: state.trans_date_m,
      trans_ref_no: state.trans_ref_no,
      trans_currency_rate: state.trans_currency_rate,
      trans_currency: state.trans_currency,
      cost_center_no: state.cost_center_no,
      created_by: getUser?.id || getUser?._id,
      is_completed: state.is_completed,
      attach_files: normalizeAttachFiles(state.attach_files).join(";"),
      branch_id: branchSelected?.value,
      tenant_id: tenantId,
      company_id: companySelected?.value,
      details: filledLines.map(
        ({ lineId, accountName, costCenterLabel, ...line }) => line,
      ),
    };
    console.log("Payload to save:", payload);
    try {
      const entryId =
        currentEntry?.id || currentEntry?._id || actionData?.entry?.id;

      if (mode === "EDIT" && !entryId) {
        setSaveValidationMsg("⚠️ خطأ: لم يتم العثور على رقم القيد للتعديل");
        return;
      }

      const result =
        mode === "EDIT"
          ? await editEntry({
              ...payload,
              id: entryId,
            })
          : await addEntry(payload);

      if (!result.success) {
        setSaveValidationMsg(
          `⚠️ خطأ في الحفظ: ${result.error || "حدث خطأ غير متوقع"}`,
        );
        return;
      }

      dispatchForm({ type: "RESET" });
      setErrors({});
      setLineErrors({});
      setSaveValidationMsg("✅ تم حفظ القيد بنجاح");
      setTimeout(() => handleClose(), 1500);
    } catch (error) {
      console.error("Save error:", error);
      setSaveValidationMsg(`⚠️ خطأ في الحفظ: ${error.message}`);
    }
  };

  useEffect(() => {
    if (mode !== "ADD") return;
    if (isPeriodClosed) return;
    if (!effectivePeriodId) return;
    if (!tenantId || !companySelected?.value || !branchSelected?.value) return;

    const fetchCode = async () => {
      try {
        setLoading(true);
        const response = await axios.post(`${baseurl}/transactions/add`, {
          fin_year_id: effectivePeriodId,
          tenant_id: tenantId,
          branch_id: branchSelected?.value,
          company_id: companySelected?.value,
          reg_date: state?.trans_date_m,
        });
        const newCode = response?.data?.data?.trans_no;
        if (newCode !== undefined && newCode !== null) {
          dispatchForm({
            type: "SET_FIELD",
            field: "trans_no",
            value: String(newCode),
          });
        }
      } catch (error) {
        console.error("Code fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCode();
  }, [
    mode,
    isPeriodClosed,
    effectivePeriodId,
    tenantId,
    companySelected?.value,
    branchSelected?.value,
    baseurl,
  ]);

  useEffect(() => {
    if (!isPeriodClosed || mode === "VIEW") return;
    setSaveValidationMsg(`⚠️ ${t("times.closedYearWarning")}`);
  }, [isPeriodClosed, mode, t]);

  useEffect(() => {
    if ((mode === "EDIT" || mode === "VIEW") && currentEntry) {
      setLoading(true);
      const timer = setTimeout(() => {
        const detailsData = Array.isArray(currentEntry?.details)
          ? currentEntry.details
          : Array.isArray(currentEntry?.lines)
            ? currentEntry.lines
            : Array.isArray(currentEntry?.detail_relation)
              ? currentEntry.detail_relation
              : [];

        const preparedDetails =
          detailsData.length > 0
            ? detailsData.map((line) => {
                const enrichedLine = enrichLineWithLabels(line);
                return normalizeLine(enrichedLine);
              })
            : [createEmptyLine()];

        const headerCostCenterNo = String(
          currentEntry?.cost_center_no || currentEntry?.costCenterNumber || "",
        ).trim();
        const headerCostCenterMeta = costCenterLookup.get(headerCostCenterNo);
        const headerCostCenterLabel =
          headerCostCenterMeta?.accountName ||
          headerCostCenterMeta?.label ||
          currentEntry?.costCenterLabel ||
          currentEntry?.cost_center_name ||
          currentEntry?.costCenterName ||
          getLocalizedLookupLabel(
            currentEntry?.treecost_relation,
            currentLanguage,
          ) ||
          getLocalizedLookupLabel(
            currentEntry?.cost_center_relation,
            currentLanguage,
          ) ||
          "";

        const editData = {
          ...initialState,
          ...currentEntry,
          trans_no: String(currentEntry?.trans_no || ""),
          trans_date_m: currentEntry?.trans_date_m || getTodayDate(),
          trans_ref_no: String(currentEntry?.trans_ref_no || ""),
          trans_desc: String(currentEntry?.trans_desc || ""),
          trans_currency: String(currentEntry?.trans_currency || "").trim(),
          cost_center_no: headerCostCenterNo,
          costCenterLabel: String(headerCostCenterLabel),
          trans_currency_rate: Number(currentEntry?.trans_currency_rate || 1),
          is_completed: Number(currentEntry?.is_completed ?? 0),
          attach_files: normalizeAttachFiles(
            currentEntry?.attach_files ||
              currentEntry?.attachFiles ||
              currentEntry?.files ||
              currentEntry?.attachments,
          ),
          details: preparedDetails,
        };
        dispatchForm({
          type: "SET_ALL",
          value: editData,
        });
        setLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }

    dispatchForm({ type: "RESET" });
    setErrors({
      trans_no: "",
      trans_ref_no: "",
    });
    setLineErrors({});
    setSaveValidationMsg("");
    setLoading(false);
  }, [mode, currentEntry]);

  useEffect(() => {
    if (mode !== "EDIT" && mode !== "VIEW") return;
    if (!Array.isArray(state.details) || state.details.length === 0) return;

    const nextDetails = state.details.map((line) =>
      normalizeLine(enrichLineWithLabels(line)),
    );
    const hasLineLabelChanges = nextDetails.some((line, index) => {
      const currentLine = state.details[index] || {};
      return (
        String(line.accountName || "").trim() !==
          String(currentLine.accountName || "").trim() ||
        String(line.costCenterLabel || "").trim() !==
          String(currentLine.costCenterLabel || "").trim()
      );
    });

    if (hasLineLabelChanges) {
      dispatchForm({
        type: "SET_FIELD",
        field: "details",
        value: nextDetails,
      });
    }

    const headerCostCenterNo = String(state.cost_center_no || "").trim();
    if (!headerCostCenterNo) return;

    const matchedHeaderCostCenter = costCenterLookup.get(headerCostCenterNo);
    const nextHeaderCostCenterLabel = String(
      matchedHeaderCostCenter?.accountName ||
        matchedHeaderCostCenter?.label ||
        state.costCenterLabel ||
        "",
    ).trim();

    if (
      nextHeaderCostCenterLabel &&
      nextHeaderCostCenterLabel !== String(state.costCenterLabel || "").trim()
    ) {
      dispatchForm({
        type: "SET_FIELD",
        field: "costCenterLabel",
        value: nextHeaderCostCenterLabel,
      });
    }
  }, [
    mode,
    state.details,
    state.cost_center_no,
    state.costCenterLabel,
    enrichLineWithLabels,
    costCenterLookup,
  ]);

  useEffect(() => {
    if (mode !== "ADD") {
      defaultCurrencyAppliedRef.current = false;
      userSelectedCurrencyRef.current = false;
      return;
    }
    const nextCurrencyId = mainCurrencyId ?? accountCurrency?.[0]?.id ?? null;
    if (nextCurrencyId === null || nextCurrencyId === undefined) return;
    const nextCurrency = accountCurrency.find(
      (currency) => String(currency.id) === String(nextCurrencyId),
    );
    const applyDefault = () => {
      dispatchForm({
        type: "SET_FIELD",
        field: "trans_currency",
        value: nextCurrencyId,
      });
      if (nextCurrency?.rate !== undefined && nextCurrency?.rate !== null) {
        dispatchForm({
          type: "SET_FIELD",
          field: "trans_currency_rate",
          value: nextCurrency.rate,
        });
      }
    };

    if (!defaultCurrencyAppliedRef.current) {
      applyDefault();
      defaultCurrencyAppliedRef.current = true;
      return;
    }

    if (
      !userSelectedCurrencyRef.current &&
      mainCurrencyId &&
      String(state.trans_currency || "") !== String(mainCurrencyId)
    ) {
      applyDefault();
    }
  }, [mode, mainCurrencyId, accountCurrency, state.trans_currency]);

  useEffect(() => {
    if (!showColumnSettings) return;

    updateColumnSettingsPosition();
    window.addEventListener("resize", updateColumnSettingsPosition);
    window.addEventListener("scroll", updateColumnSettingsPosition, true);

    return () => {
      window.removeEventListener("resize", updateColumnSettingsPosition);
      window.removeEventListener("scroll", updateColumnSettingsPosition, true);
    };
  }, [showColumnSettings, updateColumnSettingsPosition]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        columnSettingsRef.current?.contains(event.target) ||
        columnSettingsMenuRef.current?.contains(event.target)
      ) {
        return;
      }
      if (showColumnSettings) {
        setShowColumnSettings(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showColumnSettings]);

  useEffect(() => {
    if (mode === "ADD" && actionData?.code && !state.trans_no) {
      dispatchForm({
        type: "SET_FIELD",
        field: "trans_no",
        value: String(actionData.code),
      });
    }
  }, [actionData?.code, actionData?.mode]);

  return (
    <section className="p-2 h-full">
      <div className="flex flex-col bg-white rounded-lg w-full h-full overflow-hidden">
        <CardHeadeWithActions
          title={
            mode === "ADD"
              ? t("JournalEntry.newEntry")
              : mode === "EDIT"
                ? t("JournalEntry.editEntry")
                : mode === "VIEW"
                  ? t("dropdown.preview")
                  : ""
          }
          icon={"/assets/icons/notebookcolor.png"}
        >
          <div className="flex justify-end gap-2">
            <MwButton type="secondary" onClick={handleClose}>
              {t("common.cancel")}
            </MwButton>

            {canSave && (
              <MwButton
                type="primary"
                disabled={loadingSave}
                onClick={() => handleSave()}
              >
                {loadingSave ? <MwSpinnerButton /> : t("common.save")}
              </MwButton>
            )}
          </div>
        </CardHeadeWithActions>

        <div className="p-2 bg-slate-50 flex flex-col gap-2 flex-1 min-h-0 overflow-hidden">
          <div className="flex justify-start flex-wrap items-center gap-2">
            <div className="w-[12%]">
              <MwInputText
                label={t("JournalEntry.numofentry")}
                placeholder={t("JournalEntry.numofentry")}
                inputType="text"
                min={0}
                disabled={mode === "EDIT" || isReadOnly}
                value={state.trans_no}
                readOnly
                classNameInput="text-center"
                invalid={!!errors.trans_no}
                onChange={(e) => {
                  if (errors.trans_no) {
                    setErrors((prev) => ({ ...prev, trans_no: "" }));
                  }
                  dispatchForm({
                    type: "SET_FIELD",
                    field: "trans_no",
                    value: e.target.value,
                  });
                }}
              />
            </div>
            <div className="w-[12%]">
              <MwInputText
                label={t("JournalEntry.date")}
                inputType="date"
                value={state.trans_date_m || initialState.trans_date_m}
                disabled={isReadOnly}
                onChange={async (e) => {
                  const nextDate = e.target.value;
                  const currentDate = String(state.trans_date_m || "").trim();

                  dispatchForm({
                    type: "SET_FIELD",
                    field: "trans_date_m",
                    value: nextDate,
                  });

                  if (
                    mode === "EDIT" ||
                    (mode === "ADD" &&
                      nextDate &&
                      nextDate !== currentDate &&
                      effectivePeriodId &&
                      tenantId &&
                      companySelected?.value &&
                      branchSelected?.value)
                  ) {
                    try {
                      setLoading(true);
                      const response = await axios.post(
                        `${baseurl}/transactions/add`,
                        {
                          fin_year_id: effectivePeriodId,
                          tenant_id: tenantId,
                          branch_id: branchSelected?.value,
                          company_id: companySelected?.value,
                          reg_date: nextDate,
                        },
                      );

                      const newCode = response?.data?.data?.trans_no;
                      if (newCode !== undefined && newCode !== null) {
                        dispatchForm({
                          type: "SET_FIELD",
                          field: "trans_no",
                          value: String(newCode),
                        });
                        if (errors.trans_no) {
                          setErrors((prev) => ({ ...prev, trans_no: "" }));
                        }
                      }
                    } catch (error) {
                      console.error("Code fetch error:", error);
                    } finally {
                      setLoading(false);
                    }
                  }
                }}
              />
            </div>
            <div className="w-[12%]">
              <MwInputText
                label={t("JournalEntry.previosnum")}
                placeholder={t("JournalEntry.previosnum")}
                inputType="text"
                min={0}
                value={state.trans_ref_no}
                classNameInput="text-center"
                invalid={!!errors.trans_ref_no}
                disabled={isReadOnly}
                onChange={(e) => {
                  dispatchForm({
                    type: "SET_FIELD",
                    field: "trans_ref_no",
                    value: e.target.value,
                  });
                  if (errors.trans_ref_no) {
                    setErrors((prev) => ({ ...prev, trans_ref_no: "" }));
                  }
                }}
              />
            </div>
            <div className="w-[12%]">
              <MwSelector
                label={t("setting.mainCurrency")}
                dataType="labelValue"
                _data={accountCurrency}
                selectedItem={accountCurrency.find(
                  (c) => String(c.id) === String(state.trans_currency),
                )}
                disabled={isReadOnly}
                setSelectedItem={(e) => {
                  userSelectedCurrencyRef.current = true;
                  console.log("JournalEntry currency selected:", e);
                  dispatchForm({
                    type: "SET_FIELD",
                    field: "trans_currency",
                    value: e?.id || "",
                  });
                  dispatchForm({
                    type: "SET_FIELD",
                    field: "trans_currency_rate",
                    value: e?.rate ?? 1,
                  });
                }}
                withAddNew={false}
                withSearch={false}
              />
            </div>
            <div className="mt-[7.5px]  w-[30%]">
              <SelectorTree
                allowManualNumberInput={!isReadOnly}
                headerTitle={t("accounts.costCenterAccount")}
                classNameInput="text-center"
                inlineSuggestionsPosition="bottom"
                withInlineSuggestions
                allowedAccountTypes={selectionControl.costCenterAllowedTypes}
                disabled={isReadOnly}
                value={state.cost_center_no}
                label={state.costCenterLabel}
                data={costCenters}
                isAccountCenter={true}
                title={t("accounts.costCenterAccount")}
                placeholderNumber={t("accounts.accountNumber")}
                placeholderName={t("accounts.costCenterAccount")}
                onManualNumberChange={handleManualHeaderCostCenterNumberInput}
                onSelect={(e) => {
                  dispatchForm({
                    type: "SET_FIELD",
                    field: "cost_center_no",
                    value: e?.account_no || "",
                  });
                  dispatchForm({
                    type: "SET_FIELD",
                    field: "costCenterLabel",
                    value: e?.name || "",
                  });
                }}
              />
            </div>
            <div className="flex w-[15%] justify-start gap-4 items-center  ms-10  text-[#261759]">
              <div className="relative mt-1">
                <label className="text-xs font-normal px-1 mb-2 text-[#261759] pb-1 block">
                  {t("accounts.accountType")}
                </label>
                <div className="flex gap-4 items-start justify-start  text-xs">
                  <label className="flex  items-center">
                    <input
                      type="radio"
                      name="status"
                      value="1"
                      checked={state.is_completed === 1}
                      disabled={isStatusDisabled}
                      onChange={() =>
                        dispatchForm({
                          type: "SET_FIELD",
                          field: "is_completed",
                          value: 1,
                        })
                      }
                      className="me-1 cursor-pointer"
                    />
                    {t("JournalEntry.posted")}
                  </label>

                  <label className="flex  items-center">
                    <input
                      type="radio"
                      name="status"
                      value="0"
                      checked={state.is_completed === 0}
                      disabled={isStatusDisabled}
                      onChange={() =>
                        dispatchForm({
                          type: "SET_FIELD",
                          field: "is_completed",
                          value: 0,
                        })
                      }
                      className="me-1 cursor-pointer"
                    />
                    {t("JournalEntry.unposted")}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="items-center">
            <MwTextArea
              classNamePrivate={"font-normal"}
              label={t("JournalEntry.description")}
              value={state.trans_desc}
              placeholder={t("JournalEntry.description")}
              rows={1}
              disabled={isReadOnly}
              onChange={(e) => {
                if (isReadOnly) return;
                dispatchForm({
                  type: "SET_FIELD",
                  field: "trans_desc",
                  value: e.target.value,
                });
              }}
            />
          </div>
          <Tabs
            tabs={TabsData}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          {activeTab === 1 ? (
            <div className="border bg-white flex flex-col flex-1 min-h-0">
              <div
                className="flex-1 min-h-0 overflow-auto overflow-x-auto"
                onScroll={() => setShowColumnSettings(false)}
              >
                <table className="w-full min-w-[980px] text-sm border-collapse border border-slate-300">
                  <thead className="bg-slate-200  font-normal text-[#261759] text-xs">
                    <tr>
                      {visibleColumns.account && (
                        <th className="sticky top-0 z-0 font-normal bg-slate-200 px-1 py-1 text-start border border-slate-300">
                          {t("accounts.account")}
                        </th>
                      )}
                      {visibleColumns.trans_de && (
                        <th className="sticky top-0 z-0  font-normal bg-slate-200 px-1 py-1 text-start border border-slate-300">
                          {t("JournalEntry.debit")}
                        </th>
                      )}
                      {visibleColumns.trans_cr && (
                        <th className="sticky top-0 z-0  font-normal bg-slate-200 px-1 py-1 text-start border border-slate-300">
                          {t("JournalEntry.credit")}
                        </th>
                      )}
                      {visibleColumns.cost_center_no && (
                        <th className="sticky top-0 z-0  font-normal bg-slate-200 px-1 py-1 text-start border border-slate-300">
                          {t("accounts.costCenterAccount")}
                        </th>
                      )}
                      {visibleColumns.trans_notes && (
                        <th className="sticky top-0 z-0  font-normal bg-slate-200 px-1 py-1 text-start border border-slate-300">
                          {t("JournalEntry.notes")}
                        </th>
                      )}
                      <th className="sticky top-0 z-0  bg-slate-200 w-[68px] px-1 py-1 border border-slate-300 text-center">
                        <div className="flex items-center justify-center">
                          <button
                            ref={columnSettingsRef}
                            type="button"
                            onClick={() =>
                              setShowColumnSettings((prev) => !prev)
                            }
                            className="w-6 h-6 rounded border border-[#8D82B6] border-opacity-55 text-[#261759] hover:bg-slate-100"
                            title={t("setting.generalSettings")}
                          >
                            <img
                              src="/assets/icons/settings.png"
                              alt="settings"
                              className="w-3.5 h-3.5 mx-auto"
                            />
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="text-[#261759]">
                    {state.details.map((line, index) => (
                      <tr
                        key={line.lineId || index}
                        className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}
                      >
                        {visibleColumns.account && (
                          <td className="p-0 border w-80 border-slate-300 [&>div]:!mt-0">
                            <SelectorTree
                              value={line.account_no}
                              label={line.accountName}
                              data={accounts}
                              allowedAccountTypes={
                                selectionControl.accountAllowedTypes
                              }
                              title={t("accounts.accountsGuide")}
                              placeholderNumber={t(
                                "JournalEntry.accountNumber",
                              )}
                              placeholderName={t("JournalEntry.accountName")}
                              allowManualNumberInput={!isReadOnly}
                              withInlineSuggestions={true}
                              inlineSuggestionsPosition={
                                index <= 4 ? "bottom" : "top"
                              }
                              compact={true}
                              disabled={isReadOnly}
                              invalid={Boolean(lineErrors[index]?.account)}
                              onManualNumberChange={(value) =>
                                handleManualAccountNumberInput(index, value)
                              }
                              onSelect={(selected) =>
                                handleSelectAccount(index, selected)
                              }
                            />
                          </td>
                        )}

                        {visibleColumns.trans_de && (
                          <td className="p-0 w-20 border border-slate-300 [&>div]:!mt-0">
                            <MwInputText
                              inputType="text"
                              placeholder={t("JournalEntry.debit")}
                              classNameInput="text-center font-normal !h-8 !text-[11px] !py-1 !rounded-none border-0"
                              value={line.trans_de}
                              min={0}
                              invalid={Boolean(lineErrors[index]?.amount)}
                              disabled={isReadOnly}
                              onChange={(e) => {
                                if (isReadOnly) return;
                                const nextValue = e.target.value;
                                dispatchForm({
                                  type: "UPDATE_LINE_FIELD",
                                  index,
                                  field: "trans_de",
                                  value: nextValue,
                                });
                                if (String(nextValue).trim()) {
                                  dispatchForm({
                                    type: "UPDATE_LINE_FIELD",
                                    index,
                                    field: "trans_cr",
                                    value: "",
                                  });
                                  // Auto-add a new empty line if this is the last line and has a debit value
                                  if (index === state.details.length - 1) {
                                    dispatchForm({ type: "ADD_EMPTY_LINE" });
                                  }
                                }
                                setLineFieldError(index, "amount", false);
                              }}
                            />
                          </td>
                        )}

                        {visibleColumns.trans_cr && (
                          <td className="p-0 border w-20 border-slate-300 [&>div]:!mt-0">
                            <MwInputText
                              inputType="text"
                              placeholder={t("JournalEntry.credit")}
                              classNameInput="text-center !h-8 font-normal !text-[11px] !py-1 !rounded-none border-0"
                              min={0}
                              value={line.trans_cr}
                              invalid={Boolean(lineErrors[index]?.amount)}
                              disabled={isReadOnly}
                              onChange={(e) => {
                                if (isReadOnly) return;
                                const nextValue = e.target.value;
                                dispatchForm({
                                  type: "UPDATE_LINE_FIELD",
                                  index,
                                  field: "trans_cr",
                                  value: nextValue,
                                });
                                if (String(nextValue).trim()) {
                                  dispatchForm({
                                    type: "UPDATE_LINE_FIELD",
                                    index,
                                    field: "trans_de",
                                    value: "",
                                  });
                                  // Auto-add a new empty line if this is the last line and has a credit value
                                  if (index === state.details.length - 1) {
                                    dispatchForm({ type: "ADD_EMPTY_LINE" });
                                  }
                                }
                                setLineFieldError(index, "amount", false);
                              }}
                            />
                          </td>
                        )}

                        {visibleColumns.cost_center_no && (
                          <td className="p-0 border w-80 border-slate-300 [&>div]:!mt-0">
                            <SelectorTree
                              allowManualNumberInput={!isReadOnly}
                              value={line.cost_center_no}
                              label={line.costCenterLabel}
                              data={costCenters}
                              isAccountCenter={true}
                              withInlineSuggestions
                              allowedAccountTypes={
                                selectionControl.costCenterAllowedTypes
                              }
                              title={t("accounts.costCenterAccount")}
                              placeholderNumber={t("accounts.accountNumber")}
                              placeholderName={t("accounts.costCenterAccount")}
                              invalid={Boolean(lineErrors[index]?.costCenter)}
                              compact={true}
                              disabled={isReadOnly}
                              onSelect={(selected) =>
                                handleSelectCostCenter(index, selected)
                              }
                              onManualNumberChange={(value) =>
                                handleManualLineCostCenterNumberInput(
                                  index,
                                  value,
                                )
                              }
                            />
                          </td>
                        )}

                        {visibleColumns.trans_notes && (
                          <td className="p-0 border border-slate-300 [&>div]:!mt-0">
                            <MwInputText
                              classNameInput="w-full !h-8 !text-[12px] font-normal !py-1 !rounded-none border-0"
                              placeholder={t("JournalEntry.notes")}
                              value={line.trans_notes}
                              disabled={isReadOnly}
                              onChange={(e) =>
                                dispatchForm({
                                  type: "UPDATE_LINE_FIELD",
                                  index,
                                  field: "trans_notes",
                                  value: e.target.value,
                                })
                              }
                            />
                          </td>
                        )}

                        <td className="p-0 w-[68px] border border-slate-300 text-center align-middle">
                          {!isReadOnly && (
                            <button
                              className="text-red-600 hover:text-red-700 font-semibold"
                              onClick={() =>
                                dispatchForm({ type: "DELETE_LINE", index })
                              }
                            >
                              <img
                                src="/assets/icons/Trash.png"
                                alt=""
                                srcSet=""
                                className="w-3"
                              />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!isReadOnly && (
                  <div className="px-1 me-1  text-end py-0.5 border-t border-slate-200 bg-slate-50">
                    <button
                      type="button"
                      onClick={() => dispatchForm({ type: "ADD_EMPTY_LINE" })}
                      className="text-[12px] transition-all font-bold hover:underline  text-[#261759] hover:text-[#463977]"
                    >
                      {t("JournalEntry.addLine")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl overflow-auto border border-[#E2DBF5] bg-gradient-to-b from-[#FCFBFF] to-white p-4 md:p-5 shadow-sm">
              <AttachedFiles
                files={state.attach_files}
                disabled={isReadOnly}
                onChange={(nextFiles) =>
                  dispatchForm({
                    type: "SET_FIELD",
                    field: "attach_files",
                    value: normalizeAttachFiles(nextFiles),
                  })
                }
              />
            </div>
          )}
        </div>
        {showColumnSettings &&
          columnSettingsStyle &&
          createPortal(
            <div
              ref={columnSettingsMenuRef}
              style={columnSettingsStyle}
              className="rounded-md border border-[#8D82B6] border-opacity-55 bg-white p-2 shadow-lg"
            >
              {tableColumns.map((column) => (
                <label
                  key={column.key}
                  className="flex items-center gap-2 px-1 py-1 text-xs text-[#261759] hover:bg-slate-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={() =>
                      setVisibleColumns((prev) => ({
                        ...prev,
                        [column.key]: !prev[column.key],
                      }))
                    }
                  />
                  {column.label}
                </label>
              ))}
            </div>,
            document.body,
          )}
        {saveValidationMsg && (
          <div
            className={`font-semibold text-sm text-center py-2 px-3 rounded-md border ${
              saveValidationMsg.includes("✅")
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-red-50 text-red-700 border-red-200"
            }`}
          >
            {saveValidationMsg}
          </div>
        )}
        {state.details.length > 0 && activeTab === 1 ? (
          <div className="flex text-[12px] justify-between w-[98%] font-bold mx-auto  text-[#261759] py-0.5 px-2  rounded">
            <div>
              {t("JournalEntry.lines")}: {filledLines.length}
            </div>
            <div>
              {t("JournalEntry.totalDebit")}: {formatMoney(totalDebit)}{" "}
              {selectedCurrencyLabel}
            </div>
            <div>
              {t("JournalEntry.totalCredit")}: {formatMoney(totalCredit)}{" "}
              {selectedCurrencyLabel}
            </div>
            {!isBalanced ? (
              <div className="text-red-600 font-semibold text-center">
                {t("JournalEntry.unbalanced")}
              </div>
            ) : (
              <div className="text-green-800 font-normal text-center">
                {t("JournalEntry.balanced")}
              </div>
            )}
          </div>
        ) : (
          ""
        )}
      </div>

      {isDataLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg z-50">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-[#261759] rounded-full animate-spin"></div>
            <p className="text-[#261759] font-semibold text-sm">
              {t("common.loading")}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
